/**
 * Plan sözleşmesinin kabuğa bağlanması (M6 WP 6.1). `GET /plan` yanıtı `PlanProvider` adaptörüne çevrilir; `PlanGate`,
 * hızlı ekle süzgeci ve özellik sayfalarındaki bildirimler aynı adaptörü okur. Arayüz yalnız BİLGİLENDİRİR: karar
 * sunucudadır (kapalı özellikte uç `PLAN_LIMIT` döner). Yanıt beklenirken karar `pending`, alınamazsa `unknown`'dur
 * (kapalı-güvenli; `PlanGate` özelliği açmaz).
 */
import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Card, EmptyState, PageContainer, PageHeader, Spinner } from '@castinflow/ui';
import { Lock } from 'lucide-react';
import {
  PLAN_FEATURE_LABELS,
  PLAN_MODULE_LABELS,
  PLAN_MODULES,
  type ModuleDecision,
  type ModuleState,
  type PlanFeature,
  type PlanModule,
  type PlanSummary,
  type PlanUsage,
  type PlanUsageItem,
} from '@castinflow/shared';
import { apiFetch } from '../../lib/api';
import { useScope, type Scope } from '../../lib/scope';
import { usePageMeta } from '../../shell/page-meta';
import {
  PlanGate,
  PlanProvider,
  usePlan,
  type PlanAdapter,
  type PlanDecision,
} from '../shell-actions';
import { planText } from './plan-strings';

export const planKeys = {
  summary: (s: Scope) => ['t', s.tenantId, 'u', s.userId, 'plan'] as const,
  usage: (s: Scope) => ['t', s.tenantId, 'u', s.userId, 'plan', 'usage'] as const,
  /** v3.1 S7: aylık kullanım ayrıntısı */
  usageEvents: (s: Scope) => ['t', s.tenantId, 'u', s.userId, 'plan', 'usage-events'] as const,
};

export const fetchPlanSummary = () => apiFetch<PlanSummary>('/plan');

/** Sunucu yanıtından adaptör (saf; testlenir). */
export function planAdapterOf(
  status: 'pending' | 'error' | 'success',
  summary: PlanSummary | undefined,
): PlanAdapter {
  return {
    decide(feature: string): PlanDecision {
      if (status === 'pending') return 'pending';
      if (status === 'error' || summary === undefined) return 'unknown';
      const on = (summary.features as Record<string, boolean | undefined>)[feature];
      if (on === undefined) return 'unknown';
      return on ? 'enabled' : 'disabled';
    },
  };
}

export function usePlanSummary() {
  const scope = useScope();
  return useQuery({
    queryKey: scope ? planKeys.summary(scope) : ['plan', 'none'],
    queryFn: fetchPlanSummary,
    enabled: scope !== null,
    staleTime: 60_000,
    // Operatör planı değiştirdiğinde sekmeye dönen kullanıcı güncel kararı görsün
    refetchOnWindowFocus: true,
  });
}

export const fetchPlanUsage = () => apiFetch<PlanUsage>('/plan/usage');

/** Tavan + kullanım (`GET /plan/usage`; yalnız `settings.manage` — yetkisizde istenmez). Plan ekranı ve Kullanıcılar aynı sorguyu paylaşır. */
export function usePlanUsage(enabled: boolean) {
  const scope = useScope();
  return useQuery({
    queryKey: scope ? planKeys.usage(scope) : ['plan', 'usage', 'none'],
    queryFn: fetchPlanUsage,
    enabled: scope !== null && enabled,
    refetchOnWindowFocus: true,
  });
}

/** Kullanıcı tavanı satırı (Kullanıcılar › davet kartı); tavansız ya da okunamadıysa `null` */
export function usePlanUsageForSeats(enabled: boolean): PlanUsageItem | null {
  const q = usePlanUsage(enabled);
  const item = q.data?.usage.find((u) => u.key === 'users');
  if (!item?.available || item.limit === null) return null;
  return item;
}

/** Oturum kabuğu sağlayıcısı: çocuklar plan kararını `usePlan()` / `PlanGate` ile okur. */
export function SessionPlanProvider({ children }: { children: ReactNode }) {
  const q = usePlanSummary();
  const adapter = useMemo(() => planAdapterOf(q.status, q.data), [q.status, q.data]);
  return <PlanProvider value={adapter}>{children}</PlanProvider>;
}

/**
 * Özellik sayfasının başındaki bildirim: özellik planda kapalıysa görünür. Liste/okuma alanları yerinde kalır (mevcut
 * tanımlar silinmez); yalnız oluşturma eylemleri `PlanAction` ile açıklamaya döner.
 */
export function PlanFeatureNotice({ feature }: { feature: PlanFeature }) {
  const plan = usePlan();
  if (plan.decide(feature) !== 'disabled') return null;
  return <FeatureOffNotice feature={feature} />;
}

/** Yalnız özellik kapalıyken çizilir: plan adını (sağlayıcının önbelleğe aldığı) özet yanıtından okur. */
function FeatureOffNotice({ feature }: { feature: PlanFeature }) {
  const summary = usePlanSummary();
  const label = PLAN_FEATURE_LABELS[feature];
  return (
    <div data-testid="plan-feature-off" data-feature={feature}>
      <Alert
        tone="warning"
        title={
          summary.data
            ? planText.featureOff(label, summary.data.plan.name)
            : planText.featureOffNoPlan(label)
        }
      >
        {planText.featureOffBody}
      </Alert>
    </div>
  );
}

/** Oluşturma eylemi: özellik planda kapalıysa düğme yerine kısa açıklama. Karar beklenirken ya da bilinmiyorken düğme kalır. */
export function PlanAction({ feature, children }: { feature: PlanFeature; children: ReactNode }) {
  const plan = usePlan();
  if (plan.decide(feature) === 'disabled')
    return (
      <span className="text-sm text-fg-muted" data-testid="plan-action-off" data-feature={feature}>
        {planText.actionOff}
      </span>
    );
  return <>{children}</>;
}

/**
 * Yalnız "yeni oluştur" rotaları için: özellik planda açık değilse sayfa yerine `PlanGate` durumu gösterilir (adres çubuğundan
 * doğrudan gelen kullanıcı boş bir oluşturma formu yerine açıklamayı görür). `padded`: rota kendi kenar boşluğunu vermiyorsa.
 */
export function PlanRoute({
  feature,
  padded = false,
  children,
}: {
  feature: PlanFeature;
  padded?: boolean;
  children: ReactNode;
}) {
  const plan = usePlan();
  if (plan.decide(feature) === 'enabled') return <>{children}</>;
  const gate = <PlanGate feature={feature}>{children}</PlanGate>;
  return padded ? <PageContainer>{gate}</PageContainer> : gate;
}

// ---------------------------------------------------------------------------------------------------------------------
// v4.5 A-0: modül hakları (packages/shared/src/modules.ts). Arayüz yalnız BİLGİLENDİRİR; karar sunucudadır (kapalı modülün
// ucu `PLAN_LIMIT` + `reason: module_off | module_read_only` döner). Yanıt beklenirken `pending`, alınamazsa `unknown`;
// yanıtta `modules` alanı yoksa (eski sunucu) modül KAPALI sayılır. Menü yalnız `read_only` / `active` modülün öğesini çizer
// (beklerken de çizmez: kapalı-güvenli).
// ---------------------------------------------------------------------------------------------------------------------
export type ModuleUiState = 'pending' | 'unknown' | ModuleState;

/** Sunucu yanıtından modül durumu (saf; testlenir). */
export function moduleStateOf(
  status: 'pending' | 'error' | 'success',
  summary: PlanSummary | undefined,
  module: PlanModule,
): ModuleUiState {
  if (status === 'pending') return 'pending';
  if (status === 'error' || summary === undefined) return 'unknown';
  // Yanıt doğrulanmadan okunur (eski sunucu alanı göndermez); eksik modül anahtarı da kapalı sayılır
  const modules: Partial<Record<PlanModule, ModuleDecision>> = summary.modules ?? {};
  return modules[module]?.state ?? 'off';
}

/** Menüde ve rotada modülün kayıtları gösterilir mi (`read_only`: geçmiş okunur) */
export const moduleVisible = (state: ModuleUiState | undefined): boolean =>
  state === 'active' || state === 'read_only';

/** Bütün modüllerin durumu (kabuk menüsü bu kayıtla süzer) */
export function useModuleStates(): Readonly<Record<PlanModule, ModuleUiState>> {
  const q = usePlanSummary();
  return useMemo(
    () =>
      Object.fromEntries(
        PLAN_MODULES.map((m) => [m, moduleStateOf(q.status, q.data, m)]),
      ) as Record<PlanModule, ModuleUiState>,
    [q.status, q.data],
  );
}

export function useModuleState(module: PlanModule): ModuleUiState {
  const q = usePlanSummary();
  return moduleStateOf(q.status, q.data, module);
}

/** Kapalı modülün rotası: sayfa yerine açıklama (adres çubuğundan doğrudan gelen kullanıcı boş ekran görmez) */
function ModuleOffPage({ module }: { module: PlanModule }) {
  const label = PLAN_MODULE_LABELS[module];
  usePageMeta(label);
  return (
    <>
      <PageHeader title={label} />
      <Card>
        <EmptyState
          testId="module-off"
          icon={<Lock size={18} />}
          title={planText.moduleOff(label)}
          description={planText.moduleOffBody}
        />
      </Card>
    </>
  );
}

/**
 * Modül rotası: `off` → açıklama (sayfa çizilmez, uç çağrılmaz); `read_only` → üstte uyarı + sayfa (yazma eylemleri sayfada
 * kapalı); `active` → sayfa; durum alınamadıysa (`unknown`) sayfa çizilir ve karar sunucuya kalır. `testId` her durumda aynı
 * kapsayıcıdadır (ekran denetimi modül kapalıyken de ekranın çizildiğini görür).
 */
export function ModuleRoute({
  module,
  testId,
  readOnlyTestId = 'module-read-only',
  children,
}: {
  module: PlanModule;
  testId: string;
  /** Salt okunur uyarı bandının test kimliği (otomasyon için modüle özgü ad verilebilir) */
  readOnlyTestId?: string;
  children: ReactNode;
}) {
  const state = useModuleState(module);
  return (
    <div data-testid={testId} data-module-state={state} className="min-w-0">
      {state === 'pending' ? (
        <div className="flex justify-center p-8">
          <Spinner label={planText.moduleChecking} />
        </div>
      ) : state === 'off' ? (
        <ModuleOffPage module={module} />
      ) : (
        <>
          {state === 'read_only' ? (
            <div className="mb-4" data-testid={readOnlyTestId}>
              <Alert tone="warning" title={planText.moduleReadOnly}>
                {planText.moduleReadOnlyBody}
              </Alert>
            </div>
          ) : null}
          {children}
        </>
      )}
    </div>
  );
}

export { planErrorText } from '../../lib/plan-error';
